import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import InterventionAvailabilityView from 'components/intervention-availability/InterventionAvailabilityView';
import ProductView from 'components/product/ProductView';
import SectionContainer from 'components/section/SectionContainer';
import { SectionPage } from 'components/section/SectionPage';
import ProcedureBlocksView from 'components/settings/block-procedure/BlockProceduresView';
import PatientDocumentTypeView from 'components/settings/cdr-settings/PatientDocumentTypeView';
import CodeGroupsView from 'components/settings/code-group/CodeGroupsView';
import CodeView from 'components/settings/code/CodeView';
import CostCenterView from 'components/settings/cost-center/CostCenterView';
import CountryView from 'components/settings/countres/CountryView';
import DiagnosisView from 'components/settings/diagnosis/DiagnosisView';
import FileArchiveView from 'components/settings/file-archive/FileArchiveView';
import FixIssuesView from 'components/settings/fix-issues/FixIssuesView';
import InterventionView from 'components/settings/intervention/InterventionView';
import LabTestComponentView from 'components/settings/lab-test-component/LabTestComponentView';
import LabTestPanelView from 'components/settings/lab-test-panel/LabTestPanelView';
import LocationView from 'components/settings/location/LocationView';
import OneTimePasswordView from 'components/settings/one-time-password/OneTimePasswordView';
import PatientDocumentView from 'components/settings/patient-document/PatientDocumentView';
import PrinterConfigView from 'components/settings/printer-config/PrinterConfigView';
import PrintoutConfigView from 'components/settings/printout-config/PrintoutConfigView';
import QuestionSetsConfig from 'components/settings/question-sets/QuestionSetsConfig';
import ReasonForEncounterView from 'components/settings/reason-for-encounter/ReasonForEncounterView';
import ResourceGroupsView from 'components/settings/resource-groups/ResourceGroupsView';
import ResourcesView from 'components/settings/resources/ResourcesView';
import SelfServiceContainersView from 'components/settings/service-container/SelfServiceContainersView';
import StorageManagementView from 'components/settings/storage-management/StorageManagementView';
import SubCostCenterView from 'components/settings/sub-cost-center/SubCostCenterView';
import SuppliersView from 'components/settings/supplier/SuppliersView';
import TestKitReceptionOptionsView from 'components/settings/test-kit-reception/TestKitReceptionOptionsView';
import TextsView from 'components/settings/texts/TextsView';
import TreatmentReasonsView from 'components/settings/treatment-reasons/TreatmentReasonsView';
import React from 'react';
import styled from 'styled-components';
import { Module, Privilege } from 'ts-types/api.enums';

const SettingsSectionContainer = styled(SectionContainer)`
  &.settings-content > .ui.segment {
    margin-top: 0;
  }
`;

interface Props extends AuthConsumerRenderProps {
}

const SettingsHome = (props: Props) => {

  const {currentUserHasPrivilege, moduleEnabledForUser} = props;

  const pages: SectionPage[] = [
    {id: "country", uri: "/settings/countries", component: CountryView},
    {id: "treatmentReasons", uri: "/settings/treatment-reasons", component: TreatmentReasonsView},
    {id: "diagnosis", uri: "/settings/diagnosis", component: DiagnosisView},
    {id: "reasonForEncounter", uri: "/settings/reason-for-encounter", component: ReasonForEncounterView},
    {id: "procedureBlocks", uri: "/settings/procedure-blocks", component: ProcedureBlocksView},
    {id: "codeGroups", uri: "/settings/code-groups", component: CodeGroupsView},
    {id: "code", uri: "/settings/code", component: CodeView},
    {id: "costCenter", uri: "/settings/cost-center", component: CostCenterView},
    {id: "subCostCenter", uri: "/settings/sub-cost-center", component: SubCostCenterView},
    {id: "texts", uri: "/settings/texts", component: TextsView},
    {id: "patientDocument", uri: "/settings/patient-documents", component: PatientDocumentView},
    {id: "cdrSettings", uri: "/settings/cdr-settings", component: PatientDocumentTypeView},
    {id: "printoutConfig", uri: "/settings/printout-configs", component: PrintoutConfigView},
    {id: "printoutConfiguration", uri: "/settings/printer", component: PrinterConfigView},
    {id: "questionSetsConfig", uri: "/settings/question-sets-config", component: QuestionSetsConfig},
    {id: "interventionConfig", uri: "/settings/intervention-config", component: InterventionView},
    {id: "suppliers", uri: "/settings/suppliers", component: SuppliersView},
    {id: "labTestComponent", uri: "/settings/lab-test-component", component: LabTestComponentView},
    {id: "labTestPanel", uri: "/settings/lab-test-panel", component: LabTestPanelView},
    {
      id: "interventionAvailabilityConfig",
      uri: "/settings/intervention-availability-config",
      component: InterventionAvailabilityView
    },
    {id: "productConfig", uri: "/settings/product", component: ProductView},
    {id: "selfServiceContainer", uri: "/settings/self-service-container", component: SelfServiceContainersView},
    {id: "oneTimePassword", uri: "/settings/one-time-password", component: OneTimePasswordView},
    {
      id: "testKitReceptionOptions",
      uri: "/settings/testkit-reception-options",
      component: TestKitReceptionOptionsView,
      disabled: moduleEnabledForUser(Module.MODULE_TESTING)
    },

    {
      id: "resourceGroups",
      uri: "/settings/resource-groups",
      component: ResourceGroupsView
    },

    {
      id: "resources",
      uri: "/settings/resources",
      component: ResourcesView
    },
    {
      id: "location",
      uri: "/settings/location",
      component: LocationView,
      disabled: !currentUserHasPrivilege(Privilege.PX_CORE_SETTINGS_VMDL) && moduleEnabledForUser(Module.MODULE_TESTING)
    },
    {
      id: "fileArchive",
      uri: "/settings/file-archive",
      component: FileArchiveView
    }
  ];

  const userRoles = props.currentUser ? props.currentUser.roles : [];
  const isSysAdmin = userRoles.includes("ROLE_SYSTEM_ADMIN");

  if (isSysAdmin) {
    pages.push({id: "fixIssues", uri: "/settings/fix-issues", component: FixIssuesView});
    pages.push({id: "storageManagement", uri: "/settings/storage-management", component: StorageManagementView});
  }

  return (
      <SettingsSectionContainer className="settings-content" sectionPages={pages}/>
  );
};

export default withAuthContext(SettingsHome);
